import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconCheckmark1Small, IconEdit, IconPeopleAdd } from '~/ui/core/icons';

// Deep-import (not via the chat barrel) to break a ChatPromptOutlet <-> chat barrel cycle.
import { useChatStore } from '../../../chat/stores/chat-open-store';
import { createAssignReviewerItem, useAssignReviewer } from '../../api/use-assign-reviewer';
import type { JobItemDetails } from '../../api/use-job-item-details';
import { GenericReviewerContextMenuComponent } from '../../components/ReviewerComponents';
import { useTrackWorkflowTransition } from '../../hooks/analytics/use-track-workflow-transition';
import { useReviewAction } from '../../hooks/use-review-action';
import { PromptShell } from './PromptShell';
import { ReviewerLeading } from './ReviewerLeading';
import { fireTastefulConfettiAndSettle } from './TastefulConfetti';
import { useReviewerDisplay } from './use-reviewer-display';

type ItemType = 'job-level' | 'job-role';

type RoleReviewPromptProps = {
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: ItemType;
    readonly title: string;
    readonly isNewVersion: boolean;
    readonly onCompareVersions?: () => void;
};

export const RoleReviewPrompt: FunctionComponent<RoleReviewPromptProps> = ({
    architectureId,
    item,
    itemType,
    title,
    isNewVersion,
    onCompareVersions,
}) => {
    const { applicationState } = useApplicationContext();
    const translate = useTranslate();
    const openChat = useChatStore((state) => state.open);
    const { contextLine, isReviewerCurrentUser } = useReviewerDisplay(item);
    const { handleReview } = useReviewAction({ architectureId, item, itemType });
    const { mutateAsync: assignReviewer } = useAssignReviewer();
    const { trackWorkflowTransition } = useTrackWorkflowTransition({ architectureId });

    const handleLooksRight = useCallback(
        async (event: { readonly clientX: number; readonly clientY: number }): Promise<void> => {
            await fireTastefulConfettiAndSettle(event);
            await handleReview();
        },
        [handleReview],
    );

    const handleMakeChanges = useCallback((): void => {
        openChat();
    }, [openChat]);

    const handleAssignMyself = useCallback(async (): Promise<void> => {
        const id = applicationState.myUserProfile.get('id').unwrapOr('');
        const firstName = applicationState.myUserProfile.get('firstName').unwrapOr('');
        const lastName = applicationState.myUserProfile.get('lastName').unwrapOr('');
        const clusterId = item.parentId;

        if (id.length === 0 || clusterId == null) {
            return;
        }

        await assignReviewer({
            clusterId,
            item: createAssignReviewerItem(item, { id, firstName, lastName, state: 'assigned' }, item.type),
            updateType: item.reviewer === undefined ? 'create' : 'update',
        });

        trackWorkflowTransition({
            actionType: 'assign_reviewer',
            itemId: item.id,
            itemType,
            parentRoleId: itemType === 'job-level' && item.parentId != null ? item.parentId : undefined,
            targetUserId: id,
            toStatus: 'in_review',
            versionId: item.versionInfo?.version.toString() ?? '1',
        });
    }, [applicationState.myUserProfile, assignReviewer, item, itemType, trackWorkflowTransition]);

    const safeTitle = title.trim().length > 0 ? title : translate('pages.job-architecture-v2.prompts.fallback-title');
    const question = isNewVersion
        ? translate('pages.job-architecture-v2.prompts.review-question-new-version', { title: safeTitle })
        : translate('pages.job-architecture-v2.prompts.review-question', { title: safeTitle });

    const compareSecondary =
        isNewVersion && onCompareVersions != null ? (
            <Badge variant="gray" onClick={onCompareVersions}>
                {translate('pages.job-architecture-v2.prompts.compare')}
            </Badge>
        ) : null;

    const actions = isReviewerCurrentUser ? (
        <>
            {compareSecondary}
            <Badge variant="gray" onClick={handleMakeChanges}>
                <IconEdit size={11} />
                {translate('pages.job-architecture-v2.prompts.make-changes')}
            </Badge>
            <Badge variant="info" onClick={handleLooksRight}>
                <IconCheckmark1Small size={11} />
                {translate('pages.job-architecture-v2.prompts.looks-right')}
            </Badge>
        </>
    ) : (
        <>
            {compareSecondary}
            <Badge variant="gray" onClick={() => void handleAssignMyself()}>
                {translate('pages.job-architecture-v2.prompts.assign-myself')}
            </Badge>
            <DropdownMenu>
                <DropdownMenuTrigger disabled={item.actions?.canAssignReviewer !== true}>
                    <Badge
                        variant="gray"
                        onClick={() => {
                            /* noop — DropdownMenuTrigger handles the open */
                        }}
                    >
                        <IconPeopleAdd size={11} />
                        {translate('pages.job-architecture-v2.prompts.change-reviewer')}
                    </Badge>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" collisionPadding={16} side="bottom" style={{ zIndex: 3000 }}>
                    <GenericReviewerContextMenuComponent
                        item={item}
                        itemType={itemType}
                        jobArchitectureId={architectureId}
                        jobClusterId={item.parentId ?? undefined}
                        onlyAssignReviewer={true}
                    />
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );

    return (
        <PromptShell
            question={question}
            leading={<ReviewerLeading item={item} />}
            contextLine={contextLine}
            actions={actions}
        />
    );
};
